<template>
  <div class="user-management">
    <el-card shadow="never" class="main-card animate__animated animate__fadeInUp">
      <!-- 搜索栏 + 累计收益（同一行） -->
      <div class="toolbar-row">
        <div class="search-group">
          <el-input
            v-model="searchUserId"
            placeholder="用户ID"
            clearable
            size="small"
            style="width: 140px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchNickname"
            placeholder="搜索昵称"
            prefix-icon="el-icon-search"
            clearable
            size="small"
            style="width: 180px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-input
            v-model="searchInviteCode"
            placeholder="邀请码"
            prefix-icon="el-icon-collection-tag"
            clearable
            size="small"
            style="width: 160px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select
            v-model="searchStatus"
            placeholder="用户状态"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部" :value="''" />
            <el-option label="未封禁" :value="0" />
            <el-option label="已封禁" :value="1" />
          </el-select>
          <el-select
            v-model="searchRiskLevel"
            placeholder="风险等级"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部风险等级" :value="''" />
            <el-option label="无风险" :value="0" />
            <el-option label="低风险" :value="1" />
            <el-option label="中风险" :value="2" />
            <el-option label="高风险" :value="3" />
          </el-select>
          <el-select
            v-model="searchDeviceRisk"
            placeholder="设备风险"
            clearable
            size="small"
            style="width: 130px;"
            @change="handleSearch"
          >
            <el-option label="全部设备" :value="''" />
            <el-option label="正常设备" :value="0" />
            <el-option label="风险设备" :value="1" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button size="small" icon="el-icon-refresh" @click="refreshList">刷新</el-button>
          <el-button type="success" size="small" icon="el-icon-download" :loading="exportLoading" :disabled="loading" @click="handleExport">导出Excel</el-button>
        </div>
        <div class="earned-summary">
          <i class="el-icon-coin"></i>
          <span class="earned-label">累计总收益</span>
          <span class="earned-value">{{ totalEarned }}</span>
          <span class="earned-unit">金币</span>
        </div>
      </div>

      <div v-if="error" class="error-msg">{{ error }}</div>
      <el-empty
        v-else-if="!loading && users.length === 0"
        :description="searchUserId || searchNickname || searchInviteCode ? '没有符合条件的用户' : '暂无用户数据'"
      />

      <div v-else class="table-wrapper">
        <el-table
          :data="users"
          border
          stripe
          v-loading="loading"
          element-loading-text="正在加载..."
          class="data-table"
          row-key="id"
          :header-cell-style="{ background: '#fafafa', color: '#303133', fontWeight: 600 }"
        >
          <el-table-column prop="id" label="ID" width="80" align="center" />
          <el-table-column prop="openid" label="OpenID" min-width="160" show-overflow-tooltip />
          <el-table-column prop="nickname" label="昵称" min-width="140" show-overflow-tooltip />
          <el-table-column prop="avatarUrl" label="头像" width="80" align="center">
            <template slot-scope="scope">
              <el-avatar v-if="scope.row.avatarUrl" :key="scope.row.id" :src="scope.row.avatarUrl" :size="32" shape="circle" />
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="inviteCode" label="邀请码" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" effect="plain">{{ scope.row.inviteCode || '-' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="leaderId" label="代理ID" width="80" align="center" />
          <el-table-column prop="parentId" label="上级ID" width="80" align="center" />
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.status === 1" type="danger" size="small" effect="plain">封禁</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="riskLevel" label="用户风险" width="80" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.riskLevel === 1" type="success" size="small">低</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 2" type="warning" size="small">中</el-tag>
              <el-tag v-else-if="scope.row.riskLevel === 3" type="danger" size="small">高</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="deviceRisk" label="设备风险" width="90" align="center">
            <template slot-scope="scope">
              <el-tag v-if="scope.row.deviceRisk === 0" type="success" size="small" effect="plain">正常</el-tag>
              <el-tag v-else-if="scope.row.deviceRisk === 1" type="danger" size="small" effect="plain">风险</el-tag>
              <span v-else class="text-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="ip" label="IP" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">
              {{ scope.row.ip || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="city" label="城市" width="110" align="center">
            <template slot-scope="scope">
              {{ scope.row.city || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="createdAt" label="注册时间" width="160" align="center" />
          <el-table-column label="操作" width="470" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="table-ops">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-view"
                  @click="showDetail(scope.row)"
                >
                  详情
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-edit"
                  :disabled="scope.row.status === 1"
                  @click="openRateDialog(scope.row)"
                >
                  改比例
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  type="warning"
                  plain
                  icon="el-icon-coin"
                  @click="openCoinDialog(scope.row)"
                >
                  金币
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  plain
                  icon="el-icon-sort"
                  @click="openParentDialog(scope.row)"
                >
                  改上级
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  :type="scope.row.status === 0 ? 'danger' : 'success'"
                  plain
                  @click="handleToggleStatus(scope.row)"
                >
                  {{ scope.row.status === 0 ? '禁用' : '启用' }}
                </el-button>
                <el-button
                  v-if="canEdit"
                  size="mini"
                  type="danger"
                  plain
                  icon="el-icon-delete"
                  @click="handleDelete(scope.row)"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="pagination.page"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 用户详情弹窗 -->
    <el-dialog
      title="用户详情"
      :visible.sync="detailDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <div v-loading="detailLoading" element-loading-text="加载中..." class="detail-content">
        <div class="detail-header">
          <el-avatar v-if="detailUser.avatarUrl" :src="detailUser.avatarUrl" :size="56" shape="circle" />
          <div class="detail-name">{{ detailUser.nickname || '-' }}</div>
          <div class="detail-id">ID: {{ detailUser.id }}</div>
        </div>
        <div class="detail-cards">
          <div class="detail-card coin-current">
            <div class="detail-card-label">当前余额</div>
            <div class="detail-card-value">{{ detailUser.coinBalance || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
          <div class="detail-card coin-total">
            <div class="detail-card-label">累计余额</div>
            <div class="detail-card-value">{{ detailUser.totalEarnedCoins || 0 }}</div>
            <div class="detail-card-unit">金币</div>
          </div>
        </div>
        <div v-if="detailUser.status === 1 && detailUser.banDes" class="detail-ban-reason">
          <div class="ban-reason-label">封禁原因</div>
          <div class="ban-reason-text">{{ detailUser.banDes }}</div>
        </div>
        <!-- 设备风险详情 -->
        <div v-if="detailUser.deviceRisk !== null && detailUser.deviceRisk !== undefined" class="detail-risk-section">
          <div class="risk-section-title">
            <i class="el-icon-warning-outline"></i>
            设备安全检测
            <el-tag :type="detailUser.deviceRisk === 1 ? 'danger' : 'success'" size="mini" style="margin-left:8px;">
              {{ detailUser.deviceRisk === 1 ? '风险设备' : '正常设备' }}
            </el-tag>
          </div>
          <div v-if="parsedRiskDetail && parsedRiskDetail.length > 0" class="risk-items">
            <div
              v-for="(item, index) in parsedRiskDetail"
              :key="index"
              class="risk-item"
            >
              <div class="risk-item-header">
                <span class="risk-item-label">{{ item.label }}</span>
                <el-tag
                  :type="item.isRisk ? 'danger' : 'success'"
                  size="mini"
                  effect="plain"
                >
                  {{ item.isRisk ? '命中' : '正常' }}
                </el-tag>
              </div>
              <el-tooltip v-if="item.desc" :content="item.desc" placement="top-start" effect="dark">
                <span class="risk-item-desc">{{ item.desc }}</span>
              </el-tooltip>
            </div>
          </div>
          <div v-if="detailUser.deviceRiskDetail" class="risk-raw-json">
            <div class="risk-raw-title" @click="showRawJson = !showRawJson">
              <i :class="showRawJson ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
              原始风险详情 JSON
            </div>
            <pre v-if="showRawJson" class="risk-json-content">{{ formattedRiskJson }}</pre>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="canEdit" type="warning" icon="el-icon-coin" @click="openCoinDialog(detailUser)">增减金币</el-button>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 增减金币弹窗 -->
    <el-dialog
      title="增减金币"
      :visible.sync="coinDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="coinFormRef" :model="coinForm" :rules="coinRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="coinUser ? ((coinUser.nickname || '-') + '（ID: ' + coinUser.id + '）') : ''" disabled />
        </el-form-item>
        <el-form-item label="当前余额">
          <span>{{ coinBalanceLoading ? '加载中...' : (coinBalance != null ? coinBalance + ' 金币' : '-') }}</span>
        </el-form-item>
        <el-form-item label="操作类型" prop="action">
          <el-radio-group v-model="coinForm.action">
            <el-radio label="add">增加</el-radio>
            <el-radio label="deduct">扣除</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="金币数量" prop="amount">
          <el-input-number
            v-model="coinForm.amount"
            :min="1"
            :max="100000000"
            :precision="0"
            :step="100"
            controls-position="right"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input
            v-model.trim="coinForm.remark"
            type="textarea"
            :rows="2"
            maxlength="100"
            show-word-limit
            placeholder="选填，用户端明细显示此内容；不填默认「系统奖励」"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="coinDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="coinSubmitting" @click="handleCoinSubmit">确认</el-button>
      </div>
    </el-dialog>

    <!-- 超级管理员修改用户分佣比例 -->
    <el-dialog title="修改用户分佣比例" :visible.sync="rateDialogVisible" width="520px" append-to-body :close-on-click-modal="false">
      <el-form :model="rateForm" label-width="150px">
        <el-form-item label="用户">
          <el-input :value="rateForm.nickname || ('ID ' + rateForm.id)" disabled />
        </el-form-item>
        <el-form-item label="主⼴收益分佣⽐例">
          <el-input-number
            v-model="rateForm.customRate"
            :min="0"
            :max="100"
            :step="1"
            :disabled="rateForm.clearCustomRate"
            style="width:100%;"
          />
          <el-checkbox v-model="rateForm.clearCustomRate" @change="val => onClearRate(val, 'customRate')">
            清除（走全局）
          </el-checkbox>
        </el-form-item>
        <el-form-item label="副广收益分佣比例">
          <el-input-number
            v-model="rateForm.customAdPoolRate"
            :min="0"
            :max="100"
            :step="1"
            :disabled="rateForm.clearCustomAdPoolRate"
            style="width:100%;"
          />
          <el-checkbox v-model="rateForm.clearCustomAdPoolRate" @change="val => onClearRate(val, 'customAdPoolRate')">
            清除（走全局）
          </el-checkbox>
        </el-form-item>
        <el-form-item label="直推奖励比例">
          <el-input-number
            v-model="rateForm.customDirectRate"
            :min="0"
            :max="100"
            :step="1"
            :disabled="rateForm.clearCustomDirectRate"
            style="width:100%;"
          />
          <el-checkbox v-model="rateForm.clearCustomDirectRate" @change="val => onClearRate(val, 'customDirectRate')">
            清除（走全局）
          </el-checkbox>
        </el-form-item>
        <el-form-item label="间推奖励比例">
          <el-input-number
            v-model="rateForm.customIndirectRate"
            :min="0"
            :max="100"
            :step="1"
            :disabled="rateForm.clearCustomIndirectRate"
            style="width:100%;"
          />
          <el-checkbox v-model="rateForm.clearCustomIndirectRate" @change="val => onClearRate(val, 'customIndirectRate')">
            清除（走全局）
          </el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rateDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="rateSubmitting" @click="handleRateSubmit">确认修改</el-button>
      </div>
    </el-dialog>

    <!-- 修改上级 -->
    <el-dialog
      title="修改上级"
      :visible.sync="parentDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form label-width="100px">
        <el-form-item label="用户">
          <el-input :value="parentUserLabel" disabled />
        </el-form-item>
        <el-form-item label="当前上级">
          <el-input :value="parentUser && parentUser.parentId != null ? String(parentUser.parentId) : '无'" disabled />
        </el-form-item>
        <el-form-item label="操作">
          <el-radio-group v-model="parentForm.mode">
            <el-radio label="set">指定上级</el-radio>
            <el-radio label="clear">清空上级</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="parentForm.mode === 'set'" label="新上级ID">
          <el-input
            v-model.trim="parentForm.parentId"
            maxlength="10"
            placeholder="填写新上级的用户ID"
          />
        </el-form-item>
      </el-form>
      <div class="form-tip">
        <template v-if="parentForm.mode === 'set'">
          该用户及其邀请下级的代理ID会改成新上级的代理。已发放金币不回退。
        </template>
        <template v-else>
          只清空上级，代理ID不变，该用户会成为当前代理邀请链的顶端。已发放金币不回退。
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="parentDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="parentSubmitting" @click="handleParentSubmit">确认</el-button>
      </div>
    </el-dialog>

    <!-- 封禁用户弹窗 -->
    <el-dialog
      title="封禁用户"
      :visible.sync="banDialogVisible"
      width="440px"
      :close-on-click-modal="false"
      center
      append-to-body
    >
      <el-form ref="banFormRef" :model="banForm" :rules="banRules" label-width="100px">
        <el-form-item label="用户">
          <el-input :value="banUser ? banUser.nickname || banUser.id : ''" disabled />
        </el-form-item>
        <el-form-item label="封禁描述" prop="banDes">
          <el-input
            v-model.trim="banForm.banDes"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入封禁原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="banDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleBanSubmit">确认封禁</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { get, put, post, del } from '@/utils/http'
import { downloadExcel } from '@/utils/excel'

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      loading: false,
      exportLoading: false,
      error: '',
      searchUserId: '',
      searchNickname: '',
      searchInviteCode: '',
      searchStatus: '',
      searchRiskLevel: '',
      searchDeviceRisk: '',
      totalEarned: 0,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailLoading: false,
      detailUser: {},
      banDialogVisible: false,
      banUser: null,
      banForm: {
        status: 1,
        banDes: ''
      },
      banRules: {
        banDes: [{ required: true, message: '请输入封禁描述', trigger: 'blur' }]
      },
      showRawJson: false,
      riskTagDict: {},
      coinDialogVisible: false,
      coinSubmitting: false,
      coinBalanceLoading: false,
      coinBalance: null,
      coinUser: null,
      coinForm: {
        action: 'add',
        amount: 1,
        remark: ''
      },
      coinRules: {
        action: [{ required: true, message: '请选择操作类型', trigger: 'change' }],
        amount: [{ required: true, message: '请输入金币数量', trigger: 'blur' }]
      },
      rateDialogVisible: false,
      rateSubmitting: false,
      rateForm: {
        id: null,
        nickname: '',
        customRate: null,
        clearCustomRate: false,
        customAdPoolRate: null,
        clearCustomAdPoolRate: false,
        customDirectRate: null,
        clearCustomDirectRate: false,
        customIndirectRate: null,
        clearCustomIndirectRate: false
      },
      parentDialogVisible: false,
      parentSubmitting: false,
      parentUser: null,
      parentForm: {
        mode: 'set',
        parentId: ''
      }
    }
  },
  computed: {
    // 是否有修改权限（仅超级管理员 role=0）
    canEdit() {
      return parseInt(localStorage.getItem('role')) === 0
    },
    parentUserLabel() {
      if (!this.parentUser) return ''
      const name = this.parentUser.nickname || '-'
      return `${name}（ID: ${this.parentUser.id}）`
    },
    // 解析设备风险详情 JSON，返回可读的风险项列表
    // 风险标签中文释义来自后台 device_risk_tags
    parsedRiskDetail() {
      if (!this.detailUser.deviceRiskDetail) return []
      let raw = this.detailUser.deviceRiskDetail
      try {
        if (typeof raw === 'string') {
          raw = JSON.parse(raw)
        }
      } catch {
        // 解析失败，当作纯字符串处理
        raw = { tags: raw }
      }

      // 提取标签列表：支持 { tags: 'a,b' }、直接对象、纯字符串
      let tagsArr = []
      if (raw && typeof raw === 'object') {
        if (raw.tags !== undefined && typeof raw.tags === 'string') {
          tagsArr = raw.tags.split(',').filter(Boolean)
        } else if (raw.tags !== undefined && Array.isArray(raw.tags)) {
          tagsArr = raw.tags.filter(Boolean)
        } else {
          // 遍历对象 key，值为 true/1/非空的视为命中
          tagsArr = Object.keys(raw).filter(key => {
            if (key === 'tags' || key === 'deviceRisk' || key === 'deviceRiskDetail') return false
            const val = raw[key]
            return val === true || val === 1 || val === '1' || (typeof val === 'string' && val.trim() !== '')
          })
        }
      }

      // 阿里云风险标签释义来自后台 device_risk_tags
      const items = []
      tagsArr.forEach(key => {
        const cleanKey = key.trim()
        const info = this.riskTagDict[cleanKey]
        if (info) {
          items.push({
            key: cleanKey,
            label: info.label,
            isRisk: true,
            desc: info.desc
          })
        } else {
          items.push({
            key: cleanKey,
            label: cleanKey,
            isRisk: true,
            desc: '未知风险标签'
          })
        }
      })
      return items
    },
    // 格式化后的风险 JSON
    formattedRiskJson() {
      if (!this.detailUser.deviceRiskDetail) return ''
      try {
        const obj = typeof this.detailUser.deviceRiskDetail === 'string'
          ? JSON.parse(this.detailUser.deviceRiskDetail)
          : this.detailUser.deviceRiskDetail
        return JSON.stringify(obj, null, 2)
      } catch {
        return this.detailUser.deviceRiskDetail
      }
    }
  },
  mounted() {
    this.fetchUsers()
    this.fetchTotalEarned()
    this.fetchRiskTagDict()
  },
  methods: {
    async fetchRiskTagDict() {
      try {
        const result = await get('/admin/risk/aliyun-tags/dict')
        const list = result && Array.isArray(result.data) ? result.data : []
        const map = {}
        list.forEach((tag) => {
          if (!tag || !tag.tagState) return
          map[tag.tagState] = {
            label: tag.tagMean || tag.tagState,
            desc: tag.tagDesc || ''
          }
        })
        this.riskTagDict = map
      } catch (e) {
        console.error('获取阿里云风险标签字典失败:', e && e.message)
      }
    },
    async fetchTotalEarned() {
      try {
        const result = await get('/admin/users/total-earned')
        this.totalEarned = (result && result.data != null) ? result.data : 0
      } catch (e) {
        console.error('获取累计总收益失败:', e.message)
      }
    },

    async showDetail(user) {
      this.detailUser = {
        id: user.id,
        nickname: user.nickname,
        avatarUrl: user.avatarUrl
      }
      this.showRawJson = false
      this.detailDialogVisible = true
      this.detailLoading = true
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.detailUser = {
          id: data.id || user.id,
          nickname: data.nickname || user.nickname,
          avatarUrl: data.avatarUrl || user.avatarUrl,
          coinBalance: data.coinBalance,
          totalEarnedCoins: data.totalEarned,
          banDes: data.banDes || '',
          status: data.status !== undefined ? data.status : user.status,
          deviceRisk: data.deviceRisk !== undefined ? data.deviceRisk : user.deviceRisk,
          deviceRiskDetail: data.deviceRiskDetail || user.deviceRiskDetail || ''
        }
      } catch (e) {
        this.$message.error(e.message || '获取用户详情失败')
      } finally {
        this.detailLoading = false
      }
    },

    async openCoinDialog(user) {
      if (!user || !user.id) return
      this.coinUser = user
      this.coinForm = { action: 'add', amount: 1, remark: '' }
      this.coinBalance = user.coinBalance != null ? user.coinBalance : null
      this.coinDialogVisible = true
      this.coinBalanceLoading = true
      this.$nextTick(() => {
        if (this.$refs.coinFormRef) this.$refs.coinFormRef.clearValidate()
      })
      try {
        const result = await get(`/admin/users/${user.id}/balance`)
        const data = (result && result.data) ? result.data : {}
        this.coinBalance = data.coinBalance != null ? data.coinBalance : 0
      } catch (e) {
        this.$message.error(e.message || '获取用户余额失败')
      } finally {
        this.coinBalanceLoading = false
      }
    },

    async handleCoinSubmit() {
      try {
        await this.$refs.coinFormRef.validate()
      } catch {
        return
      }
      if (!this.coinUser || !this.coinUser.id) return
      this.coinSubmitting = true
      try {
        const result = await post(`/admin/users/${this.coinUser.id}/coins`, {
          action: this.coinForm.action,
          amount: this.coinForm.amount,
          remark: this.coinForm.remark
        })
        const data = (result && result.data) ? result.data : {}
        this.coinBalance = data.coinBalance != null ? data.coinBalance : this.coinBalance
        if (this.detailUser && this.detailUser.id === this.coinUser.id) {
          this.detailUser.coinBalance = this.coinBalance
        }
        const actionText = this.coinForm.action === 'add' ? '增加' : '扣除'
        this.$message.success(`已${actionText} ${this.coinForm.amount} 金币`)
        this.coinDialogVisible = false
      } catch (e) {
        this.$message.error(e.message || '操作失败')
      } finally {
        this.coinSubmitting = false
      }
    },

    openParentDialog(user) {
      if (!user || !user.id) return
      this.parentUser = user
      this.parentForm = { mode: 'set', parentId: '' }
      this.parentDialogVisible = true
    },

    async handleParentSubmit() {
      if (!this.parentUser || !this.parentUser.id) return
      const clearing = this.parentForm.mode === 'clear'
      let parentId = null
      if (!clearing) {
        const raw = String(this.parentForm.parentId || '').trim()
        if (!/^\d+$/.test(raw) || Number(raw) < 1) {
          this.$message.warning('请填写有效的上级用户ID')
          return
        }
        parentId = parseInt(raw, 10)
        if (parentId === this.parentUser.id) {
          this.$message.warning('不能把自己设为上级')
          return
        }
      }
      const tip = clearing
        ? `确认清空用户 ${this.parentUser.id} 的上级？代理ID保持不变。已发放金币不回退。`
        : `确认将用户 ${this.parentUser.id} 的上级改为 ${parentId}？该用户及其邀请下级的代理ID会改成新上级的代理。已发放金币不回退。`
      try {
        await this.$confirm(tip, '修改上级', { type: 'warning' })
      } catch {
        return
      }
      this.parentSubmitting = true
      try {
        const payload = clearing ? { clearParent: true } : { parentId }
        await put(`/admin/users/${this.parentUser.id}/parent`, payload)
        this.$message.success(clearing ? '已清空上级' : '上级已更新')
        this.parentDialogVisible = false
        await this.fetchUsers()
      } catch (e) {
        this.$message.error(e.message || '修改上级失败')
      } finally {
        this.parentSubmitting = false
      }
    },

    async fetchUsers() {
      this.loading = true
      this.error = ''
      try {
        const result = await get('/admin/users', this.buildQueryParams(true))
        const data = (result && result.data) ? result.data : {}
        this.users = Array.isArray(data.users) ? data.users : []
        this.pagination.total = data.total || 0
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
        this.pagination.page = data.page || this.pagination.page
      } catch (e) {
        this.error = e && e.message ? e.message : '获取用户列表失败'
        this.users = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },

    buildQueryParams(includePagination = true) {
      const params = {}
      if (includePagination) {
        params.page = this.pagination.page
        params.pageSize = this.pagination.pageSize
      }
      if (this.searchUserId) params.userId = this.searchUserId
      if (this.searchNickname) params.nickname = this.searchNickname
      if (this.searchInviteCode) params.inviteCode = this.searchInviteCode
      if (this.searchStatus !== '' && this.searchStatus !== null) params.status = this.searchStatus
      if (this.searchRiskLevel !== '' && this.searchRiskLevel !== null) params.riskLevel = this.searchRiskLevel
      if (this.searchDeviceRisk !== '' && this.searchDeviceRisk !== null) params.deviceRisk = this.searchDeviceRisk
      return params
    },

    formatUserStatus(status) {
      if (status === 0) return '正常'
      if (status === 1) return '封禁'
      return '-'
    },

    formatRiskLevel(level) {
      if (level === 1) return '低'
      if (level === 2) return '中'
      if (level === 3) return '高'
      return '-'
    },

    formatDeviceRisk(risk) {
      if (risk === 0) return '正常'
      if (risk === 1) return '风险'
      return '-'
    },

    async handleExport() {
      this.exportLoading = true
      try {
        const allUsers = []
        let page = 1
        const pageSize = 200
        let hasMore = true
        while (hasMore) {
          const params = { ...this.buildQueryParams(false), page, pageSize }
          const result = await get('/admin/users', params)
          const data = (result && result.data) ? result.data : {}
          const users = Array.isArray(data.users) ? data.users : []
          if (users.length > 0) {
            allUsers.push(...users)
            if (users.length < pageSize) {
              hasMore = false
            } else {
              page++
            }
          } else {
            hasMore = false
          }
          if (data.total && allUsers.length >= data.total) {
            hasMore = false
          }
        }

        if (allUsers.length === 0) {
          this.$message.warning('当前条件下没有可导出的数据')
          return
        }

        const exportData = allUsers.map(user => ({
          'ID': user.id,
          'OpenID': user.openid || '-',
          '昵称': user.nickname || '-',
          '邀请码': user.inviteCode || '-',
          '代理ID': user.leaderId != null ? user.leaderId : '-',
          '上级ID': user.parentId != null ? user.parentId : '-',
          '状态': this.formatUserStatus(user.status),
          '用户风险': this.formatRiskLevel(user.riskLevel),
          '设备风险': this.formatDeviceRisk(user.deviceRisk),
          'IP': user.ip || '-',
          '城市': user.city || '-',
          '注册时间': user.createdAt || '-'
        }))

        const searchInfo = []
        if (this.searchUserId) searchInfo.push(`用户ID_${this.searchUserId}`)
        if (this.searchNickname) searchInfo.push(`昵称_${this.searchNickname}`)
        if (this.searchInviteCode) searchInfo.push(`邀请码_${this.searchInviteCode}`)
        if (this.searchStatus !== '' && this.searchStatus !== null) searchInfo.push(`状态_${this.formatUserStatus(this.searchStatus)}`)
        if (this.searchRiskLevel !== '' && this.searchRiskLevel !== null) {
          searchInfo.push(`风险_${this.searchRiskLevel === 0 ? '无' : this.formatRiskLevel(this.searchRiskLevel)}`)
        }
        if (this.searchDeviceRisk !== '' && this.searchDeviceRisk !== null) {
          searchInfo.push(`设备_${this.formatDeviceRisk(this.searchDeviceRisk)}`)
        }
        const fileName = `用户列表_${searchInfo.length > 0 ? searchInfo.join('_') : '全部'}_${Date.now()}.xlsx`
        downloadExcel(exportData, '用户列表', fileName, [
          { wch: 10 }, { wch: 28 }, { wch: 16 }, { wch: 12 },
          { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 10 },
          { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 20 }
        ])
        this.$message.success(`成功导出 ${allUsers.length} 条记录`)
      } catch (error) {
        this.$message.error('导出失败: ' + (error.message || '未知错误'))
      } finally {
        this.exportLoading = false
      }
    },

    handleSearch() {
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.fetchUsers()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchUsers()
    },

    refreshList() {
      this.searchUserId = ''
      this.searchNickname = ''
      this.searchInviteCode = ''
      this.searchStatus = ''
      this.searchRiskLevel = ''
      this.searchDeviceRisk = ''
      this.pagination.page = 1
      this.fetchUsers()
    },

    handleToggleStatus(user) {
      if (user.status === 0) {
        // 封禁用户：弹出对话框填写封禁描述
        this.banUser = user
        this.banForm.status = 1
        this.banForm.banDes = ''
        this.banDialogVisible = true
        this.$nextTick(() => {
          if (this.$refs.banFormRef) this.$refs.banFormRef.clearValidate()
        })
      } else {
        // 解封用户：直接发送
        this.doUpdateStatus(user, 0)
      }
    },
    async doUpdateStatus(user, status, banDes = null) {
      const actionText = status === 1 ? '封禁' : '解封'
      try {
        const payload = { status }
        if (banDes) payload.banDes = banDes
        const res = await put(`/admin/users/${user.id}`, payload)
        this.$message.success(res.msg || `${actionText}成功`)
        user.status = status
        if (status === 0) user.banDes = null
      } catch (e) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    },
    openRateDialog(user) {
      this.rateForm = {
        id: user.id,
        nickname: user.nickname || '',
        customRate: user.customRate != null ? user.customRate : null,
        clearCustomRate: user.customRate == null,
        customAdPoolRate: user.customAdPoolRate != null ? user.customAdPoolRate : null,
        clearCustomAdPoolRate: user.customAdPoolRate == null,
        customDirectRate: user.customDirectRate != null ? user.customDirectRate : null,
        clearCustomDirectRate: user.customDirectRate == null,
        customIndirectRate: user.customIndirectRate != null ? user.customIndirectRate : null,
        clearCustomIndirectRate: user.customIndirectRate == null
      }
      this.rateDialogVisible = true
    },

    onClearRate(val, field) {
      if (val) {
        this.rateForm[field] = null
      }
    },

    buildRatePayload(clearFlag, valueField, clearKey, valueKey) {
      if (this.rateForm[clearFlag]) {
        return { [clearKey]: true }
      }
      if (this.rateForm[valueField] != null) {
        return { [valueKey]: this.rateForm[valueField] }
      }
      return {}
    },

    async handleRateSubmit() {
      const payload = {
        ...this.buildRatePayload('clearCustomRate', 'customRate', 'clearCustomRate', 'customRate'),
        ...this.buildRatePayload('clearCustomAdPoolRate', 'customAdPoolRate', 'clearCustomAdPoolRate', 'customAdPoolRate'),
        ...this.buildRatePayload('clearCustomDirectRate', 'customDirectRate', 'clearCustomDirectRate', 'customDirectRate'),
        ...this.buildRatePayload('clearCustomIndirectRate', 'customIndirectRate', 'clearCustomIndirectRate', 'customIndirectRate')
      }
      if (Object.keys(payload).length === 0) {
        this.$message.warning('请至少设置或清除一项比例')
        return
      }
      this.rateSubmitting = true
      try {
        // 仅超级管理员可改，走 PUT /admin/users/{id}
        const result = await put(`/admin/users/${this.rateForm.id}`, payload)
        this.$message.success((result && result.msg) || '分佣比例修改成功')
        this.rateDialogVisible = false
        const user = this.users.find(u => u.id === this.rateForm.id)
        if (user) {
          user.customRate = this.rateForm.clearCustomRate ? null : this.rateForm.customRate
          user.customAdPoolRate = this.rateForm.clearCustomAdPoolRate ? null : this.rateForm.customAdPoolRate
          user.customDirectRate = this.rateForm.clearCustomDirectRate ? null : this.rateForm.customDirectRate
          user.customIndirectRate = this.rateForm.clearCustomIndirectRate ? null : this.rateForm.customIndirectRate
        }
      } catch (e) {
        this.$message.error((e && e.message) || '修改失败')
      } finally {
        this.rateSubmitting = false
      }
    },

    async handleBanSubmit() {
      try {
        await this.$refs.banFormRef.validate()
      } catch {
        return
      }
      this.banDialogVisible = false
      await this.doUpdateStatus(this.banUser, this.banForm.status, this.banForm.banDes)
    },

    async handleDelete(user) {
      const name = user.nickname || user.openid || user.id
      let pendingCount = 0
      try {
        const checkRes = await get(`/admin/users/${user.id}/delete-check`)
        pendingCount = Number((checkRes.data && checkRes.data.pendingWithdrawalCount) || 0)
      } catch (e) {
        this.$message.error(e.message || '删除前检查失败')
        return
      }

      const tip = pendingCount > 0
        ? `该用户有 ${pendingCount} 笔待处理提现。删除后将关闭这些提现（不退币），并清零金币、解除团长/邀请关系，原支付宝/微信可重新注册。是否仍要删除？`
        : `确定删除用户「${name}」吗？将清零金币、解除团长/邀请关系，原支付宝/微信可重新注册。此操作不可恢复。`

      this.$confirm(tip, '删除用户', {
        confirmButtonText: pendingCount > 0 ? '仍要删除' : '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const force = pendingCount > 0 ? 'true' : 'false'
          await del(`/admin/users/${user.id}?force=${force}`)
          this.$message.success('删除成功')
          if (this.users.length === 1 && this.pagination.page > 1) {
            this.pagination.page -= 1
          }
          this.fetchUsers()
        } catch (e) {
          this.$message.error(e.message || '删除失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 4px;
}

.main-card {
  border-radius: 8px;
}

.table-ops {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
}

.table-ops .el-button + .el-button {
  margin-left: 0;
}

.form-tip {
  margin: 0 8px 8px 100px;
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
}

/* ===== 工具栏行 ===== */
.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e8ecf1;
}

.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex: 1;
}

/* ===== 累计收益 ===== */
.earned-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0f9ff 100%);
  border-radius: 8px;
  border: 1px solid #d1fae5;
  white-space: nowrap;
  flex-shrink: 0;
}

.earned-summary .el-icon-coin {
  font-size: 18px;
  color: #e6a23c;
  margin-right: 4px;
}

.earned-label {
  font-size: 13px;
  color: #606266;
  margin-right: 4px;
}

.earned-value {
  font-size: 20px;
  font-weight: 700;
  color: #059669;
  letter-spacing: 0.5px;
}

.earned-unit {
  font-size: 12px;
  color: #909399;
}

/* ===== 错误信息 ===== */
.error-msg {
  padding: 10px 16px;
  margin-bottom: 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 13px;
}

/* ===== 表格 ===== */
.table-wrapper {
  margin-top: 4px;
}

.data-table {
  border-radius: 6px;
  overflow: hidden;
}

.data-table::before {
  display: none;
}

.text-muted {
  color: #c0c4cc;
}

/* ===== 分页 ===== */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 8px 0;
}

/* ===== 详情弹窗 ===== */
.detail-content {
  padding: 8px 0;
}

.detail-header {
  text-align: center;
  padding: 12px 0 20px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}

.detail-name {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin-top: 10px;
}

.detail-id {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.detail-cards {
  display: flex;
  gap: 16px;
  padding: 0 4px;
}

.detail-card {
  flex: 1;
  text-align: center;
  padding: 20px 12px;
  border-radius: 10px;
  border: 1px solid;
}

.detail-card-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}

.detail-card-value {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.detail-card-unit {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

.coin-current {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.coin-current .detail-card-value {
  color: #16a34a;
}

.coin-total {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.coin-total .detail-card-value {
  color: #2563eb;
}

/* ===== 封禁原因 ===== */
.detail-ban-reason {
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
}

.ban-reason-label {
  font-size: 13px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 4px;
}

.ban-reason-text {
  font-size: 14px;
  color: #991b1b;
  line-height: 1.5;
}

.dialog-footer {
  text-align: center;
}

/* ===== 设备风险详情 ===== */
.detail-risk-section {
  margin-top: 16px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.risk-section-title .el-icon-warning-outline {
  font-size: 16px;
  color: #e6a23c;
  margin-right: 6px;
}

.risk-items {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.risk-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  min-width: 180px;
  flex: 1;
}

.risk-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.risk-item-label {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  white-space: nowrap;
}

.risk-item-desc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.risk-raw-json {
  margin-top: 12px;
  border-top: 1px dashed #dcdfe6;
  padding-top: 10px;
}

.risk-raw-title {
  font-size: 12px;
  color: #909399;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}

.risk-raw-title:hover {
  color: #409eff;
}

.risk-json-content {
  margin-top: 8px;
  padding: 12px;
  background: #2d2d2d;
  color: #f8f8f2;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

/* ===== 动画 ===== */
.main-card.animate__animated,
.data-table.animate__animated {
  --animate-duration: 0.3s;
}
</style>
